import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Tabs, Text} from '@mantine/core';
import React, {useCallback, useRef} from 'react';

import {assert} from 'Asserts';
import navigateToURL, {getPreviousURL} from 'lib/navigateToURL';
import {
  ChatSettingsModalState,
  ChatSettingsTab,
  closeChatSettingsModal,
  useAISidepanelGlobalClientState,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {chatSettingsModalUrl, isChatSettingsModalPath} from 'llm/routes/UrlUtils';
import useOnMount from 'ui-library/hooks/useOnMount';
import ModalAnchor from 'ui-library/ModalAnchor';
import Modal from 'ui-library/modals/Modal';
import enumIncludes from 'util/enumIncludes';
import * as WindowLocationUtil from 'util/WindowLocationUtil';

const useStyles = createStyles((theme) => ({
  body: {
    display: 'flex',
    flexDirection: 'row',
    height: '70vh',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabsList: {
    width: 200,
    minWidth: 200,
    padding: theme.spacing.md,
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    gap: theme.spacing.xs,
  },
  tabsPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
}));

/** Placeholder for tabs whose content hasn't been migrated into the modal yet. */
function ComingSoon(): JSX.Element {
  return (
    <Text c="dimmed" p="xl" ta="center">
      Coming soon.
    </Text>
  );
}

const TABS: Array<{value: ChatSettingsTab; label: string}> = [
  {value: ChatSettingsTab.GENERAL, label: 'General'},
  {value: ChatSettingsTab.SKILLS, label: 'Skills'},
  {value: ChatSettingsTab.CONNECTORS, label: 'AI Connectors'},
  {value: ChatSettingsTab.AGENTS, label: 'Background agents'},
];

/**
 * The single chat settings modal.
 *
 * Layout: a left sidebar nav (General / Skills / AI Connectors / Background agents) and a right pane
 * whose content changes per selected tab. General is a plain settings form; the other tabs are
 * shared listing views (item + toggle + overflow menu) that swap to a detail view when a row is
 * selected.
 *
 * The URL is the source of truth for the tab and selected row: the sidebar selection maps to
 * `/chats/settings/:tab` and a selected row to `/chats/settings/:tab/:itemId` (see AIRoutes). This
 * component reads the URL-derived state from global client state and expresses that navigation as
 * `navigateToURL(...)`.
 */
export default function ChatSettingsModal(): JSX.Element | null {
  const [panelState] = useAISidepanelGlobalClientState();
  const modal = panelState.chatSettingsModal;
  const isOpen = modal != null;

  // Keep the modal mounted/unmounted in lockstep with the URL when the user navigates with the
  // browser (back/forward). The modal's open state lives in global client state, written by the
  // AIRoutes handlers on navigation *into* a settings URL; nothing else reacts to navigation *away*.
  // If we're still open but the URL is no longer a settings route, the user navigated away (e.g. hit
  // back), so mirror that by clearing the modal state. Routing back *into* the modal is handled by
  // the AIRoutes handlers firing on popstate, so we only need to handle the "navigated away" case.
  useOnMount(() => {
    const handlePopState = (): void => {
      if (!isChatSettingsModalPath(WindowLocationUtil.getPathName())) {
        closeChatSettingsModal();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  });

  return (
    <ModalAnchor open={isOpen}>
      {/* Mount the content only while open so its mount/unmount lines up with the modal opening and
          closing; that lets it snapshot the origin URL on open and restore it on a UI-driven close. */}
      {modal != null ? <ChatSettingsModalContent modal={modal} /> : undefined}
    </ModalAnchor>
  );
}

function ChatSettingsModalContent({modal}: {modal: ChatSettingsModalState}): JSX.Element {
  const {classes} = useStyles();

  // Snapshot the URL the modal was opened from at open time
  const urlToRestoreRef = useRef<string>('/');
  useOnMount(() => {
    const previousUrl = getPreviousURL();
    urlToRestoreRef.current = previousUrl.startsWith('/') ? previousUrl : '/';

    return () => {
      // On unmount, only restore the origin URL if the URL still points at the modal. If it no
      // longer does, the unmount was caused by a navigation that already moved the address bar
      // (e.g. browser back), so overwriting it here would fight that navigation.
      if (isChatSettingsModalPath(WindowLocationUtil.getPathName())) {
        navigateToURL(urlToRestoreRef.current, {replace: true, skipTrigger: true});
      }
    };
  });

  const handleClose = useCallback(() => {
    // Clear the modal state; unmounting this component runs the cleanup above, which restores the
    // address bar to the URL the modal was opened over.
    closeChatSettingsModal();
  }, []);

  const handleTabChange = useCallback((tab: string | null) => {
    assert(
      tab != null,
      'Unexpectedly tried to deactivate tab in ChatSettingsModal without allowTabDeactivation'
    );
    assert(enumIncludes(ChatSettingsTab, tab), `Unexpected chat settings navigation, tab: ${tab}`);

    navigateToURL(chatSettingsModalUrl(tab), {replace: true});
  }, []);

  return (
    <Modal
      title="AI settings"
      onRequestClose={handleClose}
      large={true}
      bodyPadded={false}
      scrollable={false}
      dataTestAttrs={{component: 'ChatSettingsModal', element: 'modal'}}
    >
      <Box className={classes.body}>
        <Tabs
          orientation="vertical"
          value={modal.tab}
          onTabChange={handleTabChange}
          className={classes.tabsRoot}
        >
          <Tabs.List className={classes.tabsList}>
            {TABS.map((tab) => (
              <Tabs.Tab
                key={tab.value}
                value={tab.value}
                {...getTestProps({component: 'ChatSettingsModal', element: 'navItem', key: tab.value})}
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Tabs.Panel value={ChatSettingsTab.GENERAL} className={classes.tabsPanel}>
            {/* TODO: General settings content (follow-up). */}
            <ComingSoon />
          </Tabs.Panel>

          <Tabs.Panel value={ChatSettingsTab.SKILLS} className={classes.tabsPanel}>
            {/* TODO: Skills tab content (follow-up). */}
            <ComingSoon />
          </Tabs.Panel>

          <Tabs.Panel value={ChatSettingsTab.CONNECTORS} className={classes.tabsPanel}>
            {/* TODO: Connectors tab content (follow-up). */}
            <ComingSoon />
          </Tabs.Panel>

          <Tabs.Panel value={ChatSettingsTab.AGENTS} className={classes.tabsPanel}>
            {/* TODO: Background agents tab content (follow-up). */}
            <ComingSoon />
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Modal>
  );
}
